import { useState, useCallback } from 'react'
import { getAdjustedAspectRatioString } from './fileUtils'
import {
  FileErrorType,
  SupportedAssetType,
  DurationInput
} from '../../types/campaign'

type ProcessedFileData = {
  aspectRatio: string
  asset: File
  duration: DurationInput
  flightId: number
  mediaLength?: number
}

export const useFileFormatUpload = (
  onFileError: (error: FileErrorType | 'clear-error', fileName: string) => void
) => {
  const [fileDimension, setFileDimension] = useState('')
  const [videoDuration, setVideoDuration] = useState<number | undefined>(
    undefined
  )
  const [isProcessing, setIsProcessing] = useState(false)

  const validateFileMetadata = (
    width: number,
    height: number,
    fileName: string,
    objectUrl: string,
    aspectRatio: string,
    requiredAspectRatio: string,
    mediaLength?: number
  ) => {
    if (
      mediaLength !== undefined &&
      aspectRatio === '16:9' &&
      (mediaLength < 6000 || mediaLength > 90000) // Landscape max duration is 90 seconds
    ) {
      onFileError('invalid-video-duration', fileName)
      URL.revokeObjectURL(objectUrl)
      return false
    }

    if (
      mediaLength !== undefined &&
      aspectRatio === '9:16' &&
      (mediaLength < 6000 || mediaLength > 30999) // Portrait max duration is 30 seconds
    ) {
      onFileError('invalid-video-duration', fileName)
      URL.revokeObjectURL(objectUrl)
      return false
    }
    if (aspectRatio !== requiredAspectRatio) {
      onFileError('invalid-asset-aspect-ratio-for-store', fileName)
      URL.revokeObjectURL(objectUrl)
      return false
    }

    if (aspectRatio !== '16:9' && aspectRatio !== '9:16') {
      onFileError('invalid-asset-aspect-ratio', fileName)
      URL.revokeObjectURL(objectUrl)
      return false
    }

    if (aspectRatio === requiredAspectRatio) {
      if (aspectRatio === '16:9' && (width < 1920 || height < 1080)) {
        onFileError('invalid-asset-resolution', fileName)
        URL.revokeObjectURL(objectUrl)
        return false
      }

      if (aspectRatio === '9:16' && (width < 1080 || height < 1920)) {
        onFileError('invalid-asset-resolution', fileName)
        URL.revokeObjectURL(objectUrl)
        return false
      }
    }
    return true
  }

  const handleMediaLoad = useCallback(
    (
      width: number,
      height: number,
      file: File,
      objectUrl: string,
      requiredAspectRatio: string,
      duration: DurationInput,
      flightId: number,
      mediaLength: number | undefined,
      onProcessed: (data: ProcessedFileData) => void
    ) => {
      setFileDimension(`${width} x ${height}`)
      setVideoDuration(mediaLength)

      const aspectRatio = getAdjustedAspectRatioString(width, height)

      if (
        !validateFileMetadata(
          width,
          height,
          file.name,
          objectUrl,
          aspectRatio,
          requiredAspectRatio,
          mediaLength
        )
      ) {
        URL.revokeObjectURL(objectUrl)
        setIsProcessing(false)
        return
      }

      // Return processed data to component
      onProcessed({
        aspectRatio,
        asset: file,
        duration,
        flightId,
        mediaLength
      })

      URL.revokeObjectURL(objectUrl)
      setIsProcessing(false)
    },
    []
  )

  const onMediaError = (mediaType: string, objectUrl: string) => {
    console.log('Media error', mediaType)
    URL.revokeObjectURL(objectUrl)
    setIsProcessing(false)
  }

  const isSupportedAsset = (fileType: string): fileType is SupportedAssetType =>
    ['video/mp4', 'image/png', 'image/jpeg'].includes(fileType)

  const handleFile = useCallback(
    (
      file: File,
      maxDuration: number,
      requiredAspectRatio: string,
      duration: DurationInput,
      flightId: number,
      onProcessed: (data: ProcessedFileData) => void
    ) => {
      setIsProcessing(true)
      onFileError('clear-error', file.name)

      if (!isSupportedAsset(file.type)) {
        onFileError('unsupported-file-type', file.name)
        setIsProcessing(false)
        return
      }

      const objectUrl = URL.createObjectURL(file) // Create object URL, setting up media element

      if (file.type.startsWith('image/')) {
        const image = new Image()
        image.onload = () => {
          console.log('Image loaded successfully')
          const imageMediaLength = maxDuration ? maxDuration * 1000 : 6000
          handleMediaLoad(
            image.naturalWidth,
            image.naturalHeight,
            file,
            objectUrl,
            requiredAspectRatio,
            duration,
            flightId,
            imageMediaLength,
            onProcessed
          )
        }
        image.onerror = () => {
          console.log('Image failed to load')
          onMediaError('image', objectUrl)
        }
        image.src = objectUrl
      } else if (file.type.startsWith('video/')) {
        const video = document.createElement('video')
        video.onloadedmetadata = () => {
          console.log('Video metadata loaded successfully')
          handleMediaLoad(
            video.videoWidth,
            video.videoHeight,
            file,
            objectUrl,
            requiredAspectRatio,
            duration,
            flightId,
            Math.round(video.duration * 1000), // Video duration seconds needs to be converted to ms
            onProcessed
          )
        }
        video.onerror = () => {
          console.log('Video failed to load')
          onMediaError('video', objectUrl)
        }
        video.preload = 'metadata'
        video.src = objectUrl
      }
    },
    [handleMediaLoad]
  )

  return {
    handleFile,
    fileDimension,
    videoDuration,
    isProcessing
  }
}
